import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Order = Tables<'orders'>

export function OrdersListPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  useEffect(() => {
    if (!profile?.organization_id) return
    async function load() {
      let query = supabase.from('orders').select('*')
        .eq('organization_id', profile!.organization_id!)
        .order('created_at', { ascending: false })
      if (statusFilter !== 'all') query = query.eq('status', statusFilter as Tables<'orders'>['status'])
      const { data } = await query
      setOrders(data || [])
      setLoading(false)
    }
    load()
  }, [profile, statusFilter])

  return (
    <div>
      <PageHeader title="Orders" description="Track all orders through fulfilment" />

      <div className={css.tabs} style={{ marginBottom: 'var(--space-6)' }}>
        {['all', 'received', 'validated', 'acknowledged', 'in_production', 'ready_to_ship', 'shipped', 'delivered'].map(s => (
          <button key={s} className={`${css.tab} ${statusFilter === s ? css.tabActive : ''}`} onClick={() => setStatusFilter(s)}>
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p> : orders.length === 0 ? (
        <EmptyState title="No orders found" description="Orders are created by converting accepted quotes" />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead><tr><th>Reference</th><th>Status</th><th>Total</th><th>PO Number</th><th>Delivery</th><th>Confidence</th><th>Created</th></tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className={css.tableClickableRow} onClick={() => navigate(`/gaviota/orders/${o.id}`)}>
                  <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{o.reference_code}</code></td>
                  <td><StatusBadge label={o.status} /></td>
                  <td style={{ fontWeight: 500 }}>£{Number(o.total).toLocaleString()}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>{o.po_number || '-'}</td>
                  <td style={{ fontSize: 'var(--text-sm)' }}>
                    {o.promised_delivery ? new Date(o.promised_delivery).toLocaleDateString() : o.requested_delivery ? new Date(o.requested_delivery).toLocaleDateString() : '-'}
                  </td>
                  <td>
                    {o.delivery_confidence && (
                      <StatusBadge
                        label={o.delivery_confidence}
                        variant={o.delivery_confidence === 'high' ? 'success' : o.delivery_confidence === 'medium' ? 'warning' : 'error'}
                      />
                    )}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
